import { type Ref } from "react"
import { useTranslation } from "react-i18next"

import { FloatingChatShell } from "@f2e/ui/components/Chat/shell"

import CopilotLauncher from "./CopilotLauncher"
import type { CopilotSurfaceRenderProps } from "./types"

export interface CopilotFloatingProps {
  surface: CopilotSurfaceRenderProps
  isOpen: boolean
  isBackgrounded: boolean
  onOpenChange: (nextOpen: boolean) => void
  launcherRef?: Ref<HTMLButtonElement>
}

const CopilotFloating = ({
  surface: { header, content, input },
  isOpen,
  isBackgrounded,
  onOpenChange,
  launcherRef,
}: CopilotFloatingProps) => {
  const { t } = useTranslation()

  const shellProps = {
    isOpen,
    title: t("common.copilot.aria.assistantLabel"),
    launcherLabel: t("common.copilot.aria.assistantLabel"),
    closeLabel: t("common.copilot.header.minimizeAria"),
    onOpenChange,
    className: `fixed bottom-6 right-6 ${isBackgrounded ? "z-40" : "z-60"}`,
    panelClassName:
      "overflow-clip rounded-3xl bg-popover text-popover-foreground shadow-2xl ring-1 ring-border backdrop-blur-[28px] backdrop-saturate-145",
    contentClassName:
      "flex min-h-0 flex-1 flex-col overflow-hidden bg-popover px-0 py-0",
    launcher: (launcherProps: {
      isOpen: boolean
      label: string
      ariaLabel: string
      ariaControls?: string
      ariaExpanded: boolean
      onToggle: () => void
    }) => (
      <CopilotLauncher
        ref={launcherRef}
        isOpen={launcherProps.isOpen}
        onClick={launcherProps.onToggle}
        ariaLabel={launcherProps.ariaLabel}
        ariaControls={launcherProps.ariaControls}
        ariaExpanded={launcherProps.ariaExpanded}
        title={launcherProps.label}
      />
    ),
  } as const

  if (!isOpen) {
    return (
      <div data-copilot-launcher>
        <FloatingChatShell {...shellProps} />
      </div>
    )
  }

  return (
    <div data-copilot-panel>
      <FloatingChatShell
        {...shellProps}
        header={() => header}
        input={() => input}
      >
        <div className="flex min-h-0 flex-1 flex-col">{content}</div>
      </FloatingChatShell>
    </div>
  )
}

export default CopilotFloating
